import { AlertTriangle, Check, Copy } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OG_PILOT_CURSOR_PROMPT, buildCursorPromptLink } from "@/lib/cursor-deeplink";

interface ApiKeyOption {
  id: string;
  name: string;
  tokenPrefix: string;
}

interface DomainOption {
  id: string;
  hostname: string;
}

interface CodeExamplesCardProps {
  /** List of API keys to select from */
  apiKeys?: ApiKeyOption[];
  /** List of verified domains to select from */
  domains?: DomainOption[];
  /** Default issuer (domain) when no API key is selected */
  defaultIssuer?: string;
  /** Default key prefix when no API key is selected */
  defaultKeyPrefix?: string;
  /** Whether to show the API key selector dropdown */
  showApiKeySelector?: boolean;
  /** Whether to show the domain selector dropdown */
  showDomainSelector?: boolean;
  /** Whether to show the copy button */
  showCopyButton?: boolean;
  /** Card title */
  title?: string;
  /** Card description */
  description?: string;
}

const SDK_VERSIONS = {
  ruby: "0.4.1",
  javascript: "0.4.2",
  python: "0.4.1",
} as const;

export function CodeExamplesCard({
  apiKeys = [],
  domains = [],
  defaultIssuer = "yourdomain.com",
  defaultKeyPrefix = "YOUR_KEY_PREFIX",
  showApiKeySelector = true,
  showDomainSelector = false,
  showCopyButton = false,
  title = "Quick Start",
  description = "Sign a JWT with your API key and use it to generate dynamic OG images.",
}: CodeExamplesCardProps) {
  const [selectedApiKeyId, setSelectedApiKeyId] = useState<string>(apiKeys[0]?.id ?? "");
  const [selectedDomainId, setSelectedDomainId] = useState<string>(domains[0]?.id ?? "");
  const [copiedMarkdown, setCopiedMarkdown] = useState(false);

  const selectedApiKey = apiKeys.find((key) => key.id === selectedApiKeyId);
  const selectedDomain = domains.find((domain) => domain.id === selectedDomainId);
  const issuer = selectedDomain?.hostname ?? defaultIssuer;
  const keyPrefix = selectedApiKey?.tokenPrefix ?? defaultKeyPrefix;

  // Code examples with service pattern - defaults + per-page overrides
  const codeExamples = {
    ruby: `# Gemfile
gem "og_pilot_ruby", "~> ${SDK_VERSIONS.ruby}"

# Set the API key and domain in your environment variables
OG_PILOT_API_KEY=your-og-pilot-api-key
OG_PILOT_DOMAIN=${issuer}

# ============================================================
# USAGE IN YOUR CONTROLLERS / VIEWS
# ============================================================

# Generate an OG image URL (handles JWT signing automatically)
og_image = OgPilotRuby.create_image(title: "My Page Title")

# With template and additional parameters
og_image = OgPilotRuby.create_image(
  title: @post.title,
  template: "blog_post",
  author_name: @post.author.name,
  publish_date: @post.published_at.to_date.iso8601,
  image_url: @post.featured_image_url
)

# GitHub activity template (7 rows, 19 columns by default)
# Missing days are auto-filled with count: 0
github_image = OgPilotRuby.create_image(
  template: "github",
  title: "sunergos-ro",
  description: "328 code changes in the last 7 days.",
  accent_color: "#22c55e",
  contributions: [
    { date: "2026-02-15", count: 4 },
    { date: "2026-02-17", count: 2 }
  ]
)

# In ERB templates
# <meta property="og:image" content="<%= OgPilotRuby.create_image(title: @page_title) %>" />

# ============================================================
# FULL CONTROLLER EXAMPLE (with Inertia.js meta tags)
# ============================================================

class PostsController < ApplicationController
  def show
    @post = Post.find(params[:id])

    og_image = OgPilotRuby.create_image(
      title: @post.title,
      description: @post.excerpt,
      template: "blog_post",
      author_name: @post.author.name
    )

    # Works great with Inertia Rails meta tags
    inertia_meta.add [
      {tag_name: "meta", property: "og:image", content: og_image},
      {tag_name: "meta", name: "twitter:image", content: og_image}
    ]

    render inertia: "posts/show", props: {post: @post}
  end
end`,

    javascript: `// npm install og-pilot-js@^${SDK_VERSIONS.javascript}

import { configure, createImage } from "og-pilot-js";

// Set OG_PILOT_API_KEY and OG_PILOT_DOMAIN in your environment variables
// OG_PILOT_API_KEY=ogpilot-your-api-key
// OG_PILOT_DOMAIN=${issuer}

// Configure once (defaults to env vars if you skip this)
configure((config) => {
  config.apiKey = process.env.OG_PILOT_API_KEY;
  config.domain = process.env.OG_PILOT_DOMAIN ?? "${issuer}";
});

async function getOgImage(post) {
  return await createImage(
    {
      template: "blog_post",
      title: post.title,
      description: post.excerpt,
      author_name: post.author.name,
      publish_date: post.publishedAt,
      image_url: post.featuredImage,
    },
    { iat: Date.now() } // optional: refresh cache daily
  );
}

async function getGitHubOgImage() {
  return await createImage({
    template: "github",
    title: "sunergos-ro",
    description: "328 code changes in the last 7 days.",
    accent_color: "#22c55e",
    // Missing days are auto-filled with count: 0
    contributions: [
      { date: "2026-02-15", count: 4 },
      { date: "2026-02-17", count: 2 },
    ],
  });
}

// ============================================================
// USAGE IN NEXT.JS (App Router)
// ============================================================

// app/blog/[slug]/page.tsx
export async function generateMetadata({ params }): Promise<Metadata> {
  const post = await getPost(params.slug);

  return {
    openGraph: {
      images: [await getOgImage(post)],
    },
  };
}`,

    python: `# pip install og-pilot==${SDK_VERSIONS.python}

import og_pilot
from django.shortcuts import get_object_or_404, render
from .models import Post

# Uses OG_PILOT_API_KEY and OG_PILOT_DOMAIN from the environment
def blog_post(request, slug):
    post = get_object_or_404(Post, slug=slug)

    og_image = og_pilot.create_image(
        template="blog_post",
        title=post.title,
        description=post.excerpt,
        author_name=post.author.name,
        publish_date=post.published_at.strftime("%Y-%m-%d"),
    )

    github_og_image = og_pilot.create_image(
        template="github",
        title="sunergos-ro",
        description="328 code changes in the last 7 days.",
        accent_color="#22c55e",
        # Missing days are auto-filled with count: 0
        contributions=[
            {"date": "2026-02-15", "count": 4},
            {"date": "2026-02-17", "count": 2},
        ],
    )

    return render(request, "blog/post.html", {
        "post": post,
        "og_image": og_image
    })

# templates/base.html
# <meta property="og:image" content="{{ og_image }}" />`,

    php: `<?php
// composer require sunergos/og-pilot-php

use Sunergos\\OgPilot\\Facades\\OgPilot;

// .env
// OG_PILOT_API_KEY=ogpilot-your-api-key
// OG_PILOT_DOMAIN=${issuer}

// app/Http/Controllers/PostController.php
public function show(Post $post)
{
    $ogImage = OgPilot::createImage([
        "template" => "blog_post",
        "title" => $post->title,
        "description" => $post->excerpt,
        "author_name" => $post->author->name,
        "publish_date" => $post->published_at->toDateString(),
        "image_url" => $post->featured_image_url,
    ]);

    $githubImage = OgPilot::createImage([
        "template" => "github",
        "title" => "sunergos-ro",
        "description" => "328 code changes in the last 7 days.",
        "accent_color" => "#22c55e",
        // Missing days are auto-filled with count: 0
        "contributions" => [
            ["date" => "2026-02-15", "count" => 4],
            ["date" => "2026-02-17", "count" => 2],
        ],
    ]);

    return view("posts.show", [
        "post" => $post,
        "ogImage" => $ogImage,
    ]);
}

// resources/views/layouts/app.blade.php
// <meta property="og:image" content="{{ $ogImage }}" />`,
  };

  const markdown = `# OG Pilot API - Integration Guide

## Overview
JWT signing happens **in your application** (server-side). Your server signs the request payload, calls OG Pilot, and places the returned CDN image URL in the og:image meta tag. Social platforms then fetch the image from OG Pilot's CDN.

## Ruby Integration (Recommended)

For Ruby/Rails applications, use the official **og_pilot_ruby** gem which handles all JWT signing automatically:

\`\`\`ruby
${codeExamples.ruby}
\`\`\`

## JavaScript/TypeScript (og-pilot-js)
\`\`\`javascript
${codeExamples.javascript}
\`\`\`

## Python (og-pilot)
\`\`\`python
${codeExamples.python}
\`\`\`

## PHP (sunergos/og-pilot-php)
\`\`\`php
${codeExamples.php}
\`\`\`

## Other Languages

If you're using another language, you'll need to sign JWTs manually. The JWT payload structure:

\`\`\`json
{
  "iss": "${issuer}",        // A verified domain on your account
  "sub": "${keyPrefix}",     // First 8 chars of your API key
  "template": "page",        // Template type (page, blog_post, company, book, github, etc.)
  "title": "Dynamic Title",  // Dynamic content - changes per page
  "description": "Dynamic Description",
  "contributions": [         // Required for github template (missing dates auto-filled with count: 0)
    { "date": "2026-02-15", "count": 4 }
  ],
  "logo_url": "...",         // Site default (can be overridden)
  "bg_color": "#1a1a2e",     // Site default (can be overridden)
  "text_color": "#ffffff",   // Site default (can be overridden)
  "accent_color": "#22c55e"  // Optional (github template)
}
\`\`\`

## Security
**Never hardcode your API key in your codebase.** Store it securely in an environment variable (e.g., \`OG_PILOT_API_KEY\`).

## Key Points
- **Ruby users**: Use \`og_pilot_ruby\` (\`~> ${SDK_VERSIONS.ruby}\`)
- **JS/TS users**: Use \`og-pilot-js\` (\`^${SDK_VERSIONS.javascript}\`)
- **Python users**: Use \`og-pilot\` (\`==${SDK_VERSIONS.python}\`)
- **PHP users**: Use \`sunergos/og-pilot-php\`
- **Other languages**: Sign JWTs using HS256 algorithm with your full API key as the secret
- The \`sub\` claim must match the first 8 characters of your API key
- The \`iss\` claim must match a verified domain on your account
- Configure site-wide defaults once, override per-page as needed
`;
  const cursorPromptLink = buildCursorPromptLink(OG_PILOT_CURSOR_PROMPT);

  const handleCopyAsMarkdown = async () => {
    let markdownToCopy = markdown;
    let copiedFromDocsEndpoint = false;

    try {
      const response = await fetch("/docs.md", {
        headers: { Accept: "text/markdown" },
      });
      if (response.ok) {
        markdownToCopy = await response.text();
        copiedFromDocsEndpoint = true;
      }
    } catch {
      copiedFromDocsEndpoint = false;
    }

    await navigator.clipboard.writeText(markdownToCopy);
    setCopiedMarkdown(true);
    if (copiedFromDocsEndpoint) {
      toast.success("Copied latest docs markdown");
    } else {
      toast.warning("Copied fallback guide (couldn't load /docs.md)");
    }
    setTimeout(() => setCopiedMarkdown(false), 2000);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription className="mt-1.5">{description}</CardDescription>}
        </div>
        {showCopyButton && (
          <div className="flex shrink-0 items-center gap-2">
            <Button asChild variant="outline" size="sm" className="gap-2">
              <a href={cursorPromptLink}>
                <img src="/cursor/logo-dark.svg" alt="Cursor logo" className="h-4 w-4 dark:hidden" />
                <img src="/cursor/logo-light.svg" alt="Cursor logo" className="hidden h-4 w-4 dark:block" />
                Open in Cursor
              </a>
            </Button>
            <Button variant="outline" size="sm" className="gap-2" onClick={() => void handleCopyAsMarkdown()}>
              {copiedMarkdown ? <Check className="size-4 text-green-600" /> : <Copy className="size-4" />}
              Copy for LLMs
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {showApiKeySelector && apiKeys.length > 0 && (
          <div className="space-y-2">
            <Label htmlFor="code-example-key">Select an API key to prefill the examples</Label>
            <Select value={selectedApiKeyId} onValueChange={setSelectedApiKeyId}>
              <SelectTrigger id="code-example-key" className="w-full max-w-sm">
                <SelectValue placeholder="Select an API key" />
              </SelectTrigger>
              <SelectContent>
                {apiKeys.map((key) => (
                  <SelectItem key={key.id} value={key.id}>
                    {key.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {showDomainSelector && domains.length > 0 && (
          <div className="space-y-2">
            <Label htmlFor="code-example-domain">Select a verified domain for iss</Label>
            <Select value={selectedDomainId} onValueChange={setSelectedDomainId}>
              <SelectTrigger id="code-example-domain" className="w-full max-w-sm">
                <SelectValue placeholder="Select a verified domain" />
              </SelectTrigger>
              <SelectContent>
                {domains.map((domain) => (
                  <SelectItem key={domain.id} value={domain.id}>
                    {domain.hostname}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <Tabs defaultValue="ruby" className="w-full">
          <TabsList>
            <TabsTrigger value="ruby">Ruby</TabsTrigger>
            <TabsTrigger value="javascript">JavaScript</TabsTrigger>
            <TabsTrigger value="python">Python</TabsTrigger>
            <TabsTrigger value="php">PHP</TabsTrigger>
          </TabsList>

          <TabsContent value="javascript" className="mt-4">
            <pre className="bg-muted overflow-x-auto rounded-lg p-4 text-sm">{codeExamples.javascript}</pre>
          </TabsContent>

          <TabsContent value="ruby" className="mt-4">
            <pre className="bg-muted overflow-x-auto rounded-lg p-4 text-sm">{codeExamples.ruby}</pre>
          </TabsContent>

          <TabsContent value="python" className="mt-4">
            <pre className="bg-muted overflow-x-auto rounded-lg p-4 text-sm">{codeExamples.python}</pre>
          </TabsContent>

          <TabsContent value="php" className="mt-4">
            <pre className="bg-muted overflow-x-auto rounded-lg p-4 text-sm">{codeExamples.php}</pre>
          </TabsContent>
        </Tabs>

        <p className="text-muted-foreground flex items-center gap-2 text-xs">
          <AlertTriangle className="h-3 w-3 shrink-0 text-amber-500" />
          <span>
            <strong>Security:</strong> Never hardcode your API key. Store it in an environment variable like{" "}
            <code className="bg-muted rounded px-1">OG_PILOT_API_KEY</code>.
          </span>
        </p>
      </CardContent>
    </Card>
  );
}
